import React, { useState, useCallback } from "react";
import { FaCodeBranch } from "react-icons/fa";
import { useCreateBranch } from "@/api/branch";
import { useBranchStore } from "@/hooks/use-branch-store";
import { Button } from "@/components/ui/button";
import Input from "@/components/ui/input";
import { BaseModal } from "@/components/ui/base-modal";
import logger from "@/utils/logger";

interface CreateBranchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Modal for creating a new branch.
 */
const CreateBranchModal: React.FC<CreateBranchModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const createBranchMutation = useCreateBranch();
  const { setActiveBranch } = useBranchStore();

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      const trimmedName = name.trim();
      if (!trimmedName) {
        setError("Le nom de la branche est requis");
        return;
      }

      if (trimmedName.toLowerCase() === "main") {
        setError("Le nom 'main' est réservé");
        return;
      }

      // Validate branch name format (alphanumeric, dashes, underscores)
      if (!/^[a-zA-Z0-9_-]+$/.test(trimmedName)) {
        setError("Le nom ne peut contenir que des lettres, chiffres, tirets et underscores");
        return;
      }

      try {
        const newBranch = await createBranchMutation.mutateAsync({
          name: trimmedName,
        });
        
        // Switch to the new branch
        setActiveBranch(newBranch.id);
        
        // Reset form and close
        setName("");
        onSuccess?.();
        onClose();
      } catch (err: any) {
        logger.error("Failed to create branch:", err);
        setError(err?.response?.data?.detail || err?.message || "Erreur lors de la création");
      }
    },
    [name, createBranchMutation, setActiveBranch, onSuccess, onClose]
  );

  const handleClose = useCallback(() => {
    setName("");
    setError(null);
    onClose();
  }, [onClose]);

  return (
    <BaseModal
      open={isOpen}
      onClose={handleClose}
      title="Nouvelle branche"
      icon={<FaCodeBranch className="text-[var(--arc-accent)]" />}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="branch-name"
            className="block text-sm font-medium text-content-secondary mb-1"
          >
            Nom de la branche
          </label>
          <Input
            id="branch-name"
            type="text"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            placeholder="feature-my-feature"
            autoFocus
            className="w-full"
          />
          {error && (
            <p className="mt-1 text-xs text-red-500">{error}</p>
          )}
          <p className="mt-1 text-xs text-content-tertiary">
            La branche sera créée à partir de la branche active actuelle.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={createBranchMutation.isPending || !name.trim()}
          >
            {createBranchMutation.isPending ? "Création..." : "Créer"}
          </Button>
        </div>
      </form>
    </BaseModal>
  );
};

CreateBranchModal.displayName = "CreateBranchModal";

export default CreateBranchModal;
